/**
 * <DynamicPreferenceForm /> - Schema-driven preference renderer (Pass 3)
 *
 * Consumes GET /api/sync/preference-card-schema?category={category_id}
 * Renders every section + field defined in admin master data.
 * This is THE component that unlocks the "Zero Hardcoding" rule across
 * all 11 categories. Adding category #12+ requires no frontend code.
 *
 * Field types supported:
 *   - multi_select          → option pills (multi)
 *   - single_select         → option pills (single)
 *   - long_text             → textarea (one notes per category)
 *   - multi_image_upload    → upload-only image grid (no URL paste)
 *   - multi_select_master   → grouped picker from master_products / master_brands
 *
 * Props:
 *   category        - required, the category_id (e.g. "makeup")
 *   value           - current answers map { field_id: [...selected] | "text" | [...urls] }
 *   onChange        - (next_value) => void
 *   serviceContext  - { instructor_linked: bool } controls instructor merge fields
 *   hostOverrides   - { field_id: { visible: bool, required: bool } }
 *   testIdPrefix    - for testing
 */
import { useEffect, useState, useMemo } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fetchSchema = async (categoryId) => {
  const r = await fetch(`${API}/sync/preference-card-schema?category=${encodeURIComponent(categoryId)}`);
  if (!r.ok) throw new Error(`Schema fetch failed: ${r.status}`);
  return r.json();
};

// ---------- Field renderers ----------
function OptionPills({ field, selected, onSelect, isMulti, testIdPrefix }) {
  const options = field.options_enriched || field.options || [];
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const hasImages = norm.some((o) => o.image_url);
  const hasSwatches = !hasImages && norm.some((o) => o.swatch_color);
  const containerCls = hasImages ? "grid grid-cols-3 gap-2" : "flex flex-wrap gap-2";

  return (
    <div className={containerCls} data-testid={`${testIdPrefix}-${field.field_id || field.key}-options`}>
      {norm.map((opt) => {
        const v = opt.value ?? opt.id ?? opt.label;
        const active = isMulti ? (selected || []).includes(v) : selected === v;
        const onClick = () => {
          if (isMulti) {
            const cur = selected || [];
            onSelect(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
          } else {
            onSelect(active ? null : v);
          }
        };
        const safeKey = String(v).replace(/\s+/g, "-").toLowerCase();
        if (hasImages) {
          return (
            <button
              type="button"
              key={v}
              onClick={onClick}
              title={opt.tooltip || opt.label}
              className={cn(
                "relative rounded-xl overflow-hidden border-2 transition-all",
                active ? "border-black shadow-md" : "border-stone-200 hover:border-stone-400"
              )}
              data-testid={`${testIdPrefix}-${field.field_id || field.key}-opt-${safeKey}`}
            >
              <div className="aspect-square bg-stone-100">
                {opt.image_url ? (
                  <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-full" style={opt.swatch_color?.startsWith("linear-gradient") ? { backgroundImage: opt.swatch_color } : opt.swatch_color ? { background: opt.swatch_color } : undefined} />
                )}
              </div>
              <div className="px-2 py-1.5 text-xs font-medium text-stone-900 text-left">{opt.label}</div>
              {active && (
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs">✓</div>
              )}
            </button>
          );
        }
        if (hasSwatches && opt.swatch_color) {
          const isGradient = opt.swatch_color.startsWith("linear-gradient");
          return (
            <button
              type="button"
              key={v}
              onClick={onClick}
              title={opt.tooltip || opt.label}
              className={cn(
                "flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full text-sm border-2 transition-all",
                active ? "bg-black text-white border-black" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
              )}
              data-testid={`${testIdPrefix}-${field.field_id || field.key}-opt-${safeKey}`}
            >
              <span
                className="w-5 h-5 rounded-full border border-stone-300 flex-shrink-0"
                style={isGradient ? { backgroundImage: opt.swatch_color } : { background: opt.swatch_color }}
              />
              {opt.label}
            </button>
          );
        }
        return (
          <button
            type="button"
            key={v}
            onClick={onClick}
            title={opt.tooltip || opt.label}
            className={cn(
              "px-3 py-1.5 text-sm rounded-full border transition-all",
              active
                ? "bg-black text-white border-black"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            )}
            data-testid={`${testIdPrefix}-${field.field_id || field.key}-opt-${safeKey}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function NotesField({ field, value, onChange, testIdPrefix }) {
  return (
    <textarea
      placeholder={field.placeholder || "Anything else you'd like to share?"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:border-black"
      data-testid={`${testIdPrefix}-${field.field_id}-textarea`}
    />
  );
}

function ImageUploadField({ field, value, onChange, testIdPrefix }) {
  const images = value || [];
  const max = field.max_images || 12;
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - images.length;
    const toAdd = files.slice(0, remaining);
    // For now, store as data URLs; integration with Media Library upload happens server-side
    const urls = await Promise.all(toAdd.map(f => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.readAsDataURL(f);
    })));
    onChange([...images, ...urls]);
  };
  return (
    <div data-testid={`${testIdPrefix}-${field.field_id}-uploader`}>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {images.map((src, i) => (
          <div key={i} className="relative aspect-square bg-stone-100 rounded-lg overflow-hidden group">
            <img src={src} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(images.filter((_, j) => j !== i))}
              className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full text-xs opacity-0 group-hover:opacity-100"
              data-testid={`${testIdPrefix}-${field.field_id}-remove-${i}`}
            >×</button>
          </div>
        ))}
        {images.length < max && (
          <label className="aspect-square border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center text-stone-400 hover:border-black hover:text-black cursor-pointer text-xs">
            + Upload
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFile} data-testid={`${testIdPrefix}-${field.field_id}-input`} />
          </label>
        )}
      </div>
      <p className="text-xs text-stone-400">Upload only · no URLs · {images.length}/{max}</p>
    </div>
  );
}

function MasterPickerField({ field, value, onChange, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const isBrands = field.master_source === "master_brands";
        const params = new URLSearchParams(field.filter || {});
        const path = isBrands ? "master-brands" : "master-products";
        const r = await fetch(`${API}/${path}?${params}`, { headers });
        if (!r.ok) throw new Error("master fetch failed");
        const d = await r.json();
        if (!cancel) setItems(d.products || d.brands || d.items || (Array.isArray(d) ? d : []));
      } catch (_e) {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [field.master_source, JSON.stringify(field.filter || {})]);

  const grouped = useMemo(() => {
    if (!field.grouped_by) return { all: items };
    const by = {};
    items.forEach(it => {
      const g = it[field.grouped_by] || "Other";
      (by[g] = by[g] || []).push(it);
    });
    return by;
  }, [items, field.grouped_by]);

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-stone-400" />;
  const selected = value || [];

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-${field.field_id}-master-picker`}>
      {(field.groups || Object.keys(grouped)).map((g) => (
        <div key={g}>
          {field.grouped_by && <div className="text-xs font-medium text-stone-600 mb-1.5">{g}</div>}
          <div className="flex flex-wrap gap-2">
            {(grouped[g] || []).map((it) => {
              const id = it.product_id || it.brand_id || it.id;
              const label = it.brand ? `${it.brand} · ${it.name || it.shade || ""}` : (it.name || id);
              const active = selected.includes(id);
              return (
                <button
                  type="button"
                  key={id}
                  onClick={() => onChange(active ? selected.filter(x => x !== id) : [...selected, id])}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-full border transition-all",
                    active
                      ? "bg-black text-white border-black"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  )}
                  data-testid={`${testIdPrefix}-${field.field_id}-item-${id}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Main component ----------
export default function DynamicPreferenceForm({
  category,
  value = {},
  onChange,
  serviceContext = {},
  hostOverrides = {},
  testIdPrefix = "dynpref",
}) {
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 9.48.3 - Section collapsed-state map. "bridal" (Wedding Details)
  // defaults to collapsed so users planning weddings can opt-in rather than
  // see the whole bridal logistics tree by default on every preference card.
  const [collapsed, setCollapsed] = useState({ bridal: true });

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchSchema(category)
      .then((s) => { if (!cancel) { setSchema(s); setError(null); } })
      .catch((e) => { if (!cancel) setError(e.message); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [category]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-rose-600 p-4 bg-rose-50 rounded-lg" data-testid={`${testIdPrefix}-error`}>
        Schema error: {error}
      </div>
    );
  }
  if (!schema) return null;

  const setField = (fieldId, v) => onChange?.({ ...value, [fieldId]: v });

  const isFieldVisible = (f) => {
    const override = hostOverrides[f.field_id];
    if (override?.visible === false) return false;
    if (f.shown_when && f.shown_when["service.instructor_linked"] === true) {
      if (!serviceContext.instructor_linked) return false;
    }
    if (f.shown_when && f.shown_when["service.is_bridal"] === true) {
      if (!serviceContext.is_bridal) return false;
    }
    return f.default_visible !== false;
  };

  return (
    <div className="space-y-8" data-testid={`${testIdPrefix}-form`}>
      {/* Tags shown when instructor-linked */}
      {serviceContext.instructor_linked && schema.card_tags_when_instructor_linked?.length > 0 && (
        <div className="flex gap-2" data-testid={`${testIdPrefix}-tags`}>
          {schema.card_tags_when_instructor_linked.map((t) => (
            <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-700 border border-stone-200">
              {t}
            </span>
          ))}
        </div>
      )}

      {schema.sections.map((section) => {
        const fields = section.fields.filter(isFieldVisible);
        if (fields.length === 0) return null;
        // Iter 9.48.3 - "bridal" section (Wedding Details) is a collapsible
        // accordion that defaults to CLOSED. Other sections render flat.
        const isCollapsible = section.section_id === "bridal" || section.collapsible === true;
        const isOpen = isCollapsible ? !collapsed[section.section_id] : true;
        return (
          <section key={section.section_id} className="space-y-4" data-testid={`${testIdPrefix}-section-${section.section_id}`}>
            {isCollapsible ? (
              <button
                type="button"
                onClick={() => setCollapsed((c) => ({ ...c, [section.section_id]: !c[section.section_id] }))}
                className="w-full flex items-center justify-between text-left group"
                data-testid={`${testIdPrefix}-section-${section.section_id}-toggle`}
                aria-expanded={isOpen}
              >
                <h3 className="text-sm font-semibold tracking-wide text-stone-900 uppercase">
                  {section.section_label}
                </h3>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-stone-500 transition-transform duration-200",
                    isOpen ? "rotate-180" : "rotate-0"
                  )}
                />
              </button>
            ) : (
              <h3 className="text-sm font-semibold tracking-wide text-stone-900 uppercase">
                {section.section_label}
              </h3>
            )}
            {isOpen && (
            <div className="space-y-5">
              {fields.map((f) => {
                const required = hostOverrides[f.field_id]?.required ?? f.default_required;
                const intakeOnly = f.appointment_intake_only === true;
                return (
                  <div key={f.field_id} data-testid={`${testIdPrefix}-field-${f.field_id}`}>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {f.label}
                      {required && <span className="text-rose-500 ml-1">*</span>}
                      {intakeOnly && (
                        <span
                          className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-amber-50 text-amber-700 border border-amber-200"
                          data-testid={`${testIdPrefix}-field-${f.field_id}-intake-badge`}
                          title="Saved with this appointment only - not added to your permanent preferences"
                        >
                          Per appointment
                        </span>
                      )}
                    </label>
                    {f.type === "multi_select" && (
                      <OptionPills field={f} selected={value[f.field_id]} onSelect={(v) => setField(f.field_id, v)} isMulti testIdPrefix={testIdPrefix} />
                    )}
                    {f.type === "single_select" && (
                      <OptionPills field={f} selected={value[f.field_id]} onSelect={(v) => setField(f.field_id, v)} isMulti={false} testIdPrefix={testIdPrefix} />
                    )}
                    {f.type === "long_text" && (
                      <NotesField field={f} value={value[f.field_id]} onChange={(v) => setField(f.field_id, v)} testIdPrefix={testIdPrefix} />
                    )}
                    {f.type === "multi_image_upload" && (
                      <ImageUploadField field={f} value={value[f.field_id]} onChange={(v) => setField(f.field_id, v)} testIdPrefix={testIdPrefix} />
                    )}
                    {f.type === "multi_select_master" && (
                      <MasterPickerField field={f} value={value[f.field_id]} onChange={(v) => setField(f.field_id, v)} testIdPrefix={testIdPrefix} />
                    )}
                    {f.type === "date" && (
                      <input
                        type="date"
                        value={value[f.field_id] || ""}
                        onChange={(e) => setField(f.field_id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-black"
                        data-testid={`${testIdPrefix}-${f.field_id}-date`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export { DynamicPreferenceForm };
